import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import {
  UserIcon,
  ArrowUpRightIcon,
  ArrowDownRightIcon,
  MapPinIcon,
} from '@heroicons/react/24/solid';
import { FORM_ERROR } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { HotelForm as Form } from 'hotel/HotelForm';
import { useSearchParams, useNavigate } from 'hotel/navigation';
import { useEffect } from 'react';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { useDispatch } from 'react-redux';

import { TealButton } from 'components/Button';
import SelectField from 'components/Inputs/FormInputs/SelectField';
import TextField from 'components/Inputs/FormInputs/TextField';
import {
  setQuantities,
  setRoomConfigurations,
  reset as resetGroupDetails,
  setSearchParams,
} from 'store/groupDetailsSlice';
import { useAppSelector } from 'store/store';
import { validateRoomQuantities } from 'utilities/validators';

const MAX_CONFIGURATIONS_PER_ROOM_TYPE = 2;

// Using RoomConfiguration from store

// Form values use string for input fields
interface FormRoomConfiguration {
  rooms: string;
  guests: string;
}

export interface FormValues {
  single: FormRoomConfiguration[];
  double: FormRoomConfiguration[];
  suite: FormRoomConfiguration[];
}

const GroupRooms = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const existingRooms = useAppSelector(
    (state) => state.groupDetails.quantities,
  );
  const storedSearchParams = useAppSelector(
    (state) => state.groupDetails.searchParams,
  );
  const dispatch = useDispatch();

  const { checkInDate, checkOutDate, description, roomQuantity } =
    Object.fromEntries(searchParams.entries());

  const minRooms = Number(roomQuantity || 9); // Default to minimum 9 rooms for groups

  // Check if search params have changed and reset room configurations if needed
  useEffect(() => {
    const currentParamsString = searchParams.toString();
    if (storedSearchParams && storedSearchParams !== currentParamsString) {
      // Search params have changed, reset room configurations
      dispatch(resetGroupDetails());
    }
    // Store current search params
    dispatch(setSearchParams(currentParamsString));
  }, [searchParams, storedSearchParams, dispatch]);

  // Calculate initial configurations based on existing rooms
  const getInitialConfigurations = () => {
    // If we have existing room configurations, use them
    if (
      existingRooms.single > 0 ||
      existingRooms.double > 0 ||
      existingRooms.suite > 0
    ) {
      return {
        single:
          existingRooms.single > 0
            ? [{ rooms: String(existingRooms.single), guests: '1' }]
            : [{ rooms: '0', guests: '1' }],
        double:
          existingRooms.double > 0
            ? [{ rooms: String(existingRooms.double), guests: '2' }]
            : [{ rooms: '0', guests: '2' }],
        suite:
          existingRooms.suite > 0
            ? [{ rooms: String(existingRooms.suite), guests: '1' }]
            : [{ rooms: '0', guests: '1' }],
      };
    }

    // Default configuration for new group bookings
    return {
      single: [{ rooms: '0', guests: '1' }],
      double: [{ rooms: '0', guests: '2' }],
      suite: [{ rooms: '0', guests: '1' }],
    };
  };

  const calculateTotalsFromConfigs = (configs: FormRoomConfiguration[]) => {
    return configs.reduce(
      (acc, config) => {
        const rooms = Number(config.rooms || 0);
        const guests = Number(config.guests || 0);
        return {
          rooms: acc.rooms + rooms,
          guests: acc.guests + rooms * guests,
        };
      },
      { rooms: 0, guests: 0 },
    );
  };

  const handleFormSubmit = (values: FormValues) => {
    // Calculate totals from configurations
    const singleTotals = calculateTotalsFromConfigs(values.single);
    const doubleTotals = calculateTotalsFromConfigs(values.double);
    const suiteTotals = calculateTotalsFromConfigs(values.suite);

    const totalRooms =
      singleTotals.rooms + doubleTotals.rooms + suiteTotals.rooms;

    if (totalRooms === 0) {
      return {
        [FORM_ERROR]: `Please select at least one room`,
      };
    }

    if (totalRooms < minRooms) {
      return {
        [FORM_ERROR]: `Group bookings require a minimum of ${minRooms} rooms. You have selected ${totalRooms} rooms.`,
      };
    }

    // Transform configurations to quantities for Redux
    // Keep all double rooms as doubles, even with single occupancy
    const finalSingleCount = singleTotals.rooms;
    const finalDoubleCount = doubleTotals.rooms; // All doubles stay as doubles

    dispatch(
      setQuantities({
        single: finalSingleCount,
        double: finalDoubleCount,
        suite: suiteTotals.rooms,
      }),
    );

    // Store room configurations for display in request details
    const roomConfigurations = {
      single: values.single
        .filter((config) => Number(config.rooms || 0) > 0)
        .map((config) => ({
          rooms: Number(config.rooms || 0),
          guests: Number(config.guests || 0),
        })),
      double: values.double
        .filter((config) => Number(config.rooms || 0) > 0)
        .map((config) => ({
          rooms: Number(config.rooms || 0),
          guests: Number(config.guests || 0),
        })),
      suite: values.suite
        .filter((config) => Number(config.rooms || 0) > 0)
        .map((config) => ({
          rooms: Number(config.rooms || 0),
          guests: Number(config.guests || 0),
        })),
    };

    dispatch(setRoomConfigurations(roomConfigurations));

    searchParams.set('guestQuantity', '1');
    searchParams.set('roomQuantity', '1');
    navigate(`/search/?${searchParams.toString()}`);
  };

  return (
    <div className="mb-40 flex items-start justify-center p-4 md:p-6 xl:px-0">
      <div className="mx-auto max-w-7xl flex-1">
        <div className="mb-10 overflow-hidden rounded-lg bg-white shadow-sm">
          <div className="bg-white p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="font-oswald text-xl font-bold text-gray-900 sm:text-2xl">
                  Trip Details
                </p>
              </div>
              <div>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200"></div>
          <dl className="mx-auto grid grid-cols-1 gap-px bg-gray-200 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 bg-white px-4 py-6 sm:px-6 xl:px-8">
              <dt className="text-sm font-medium text-gray-500">Location</dt>
              <dd className="flex w-full flex-none items-center gap-2 font-arimo text-lg font-medium tracking-tight text-gray-900 xl:text-xl">
                <MapPinIcon className="size-[1em] text-accentGreen" />
                {description}
              </dd>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 bg-white px-4 py-6 sm:px-6 xl:px-8">
              <dt className="text-sm font-medium text-gray-500">
                Minimum Rooms
              </dt>
              <dd className="flex w-full flex-none items-center gap-2 font-arimo text-lg font-medium tracking-tight text-gray-900 xl:text-xl">
                <UserIcon className="size-[1em] text-accentGreen" />
                {minRooms}
              </dd>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 bg-white px-4 py-6 sm:px-6 xl:px-8">
              <dt className="text-sm font-medium text-gray-500">
                Check-in Date
              </dt>
              <dd className="flex w-full flex-none items-center gap-2 font-arimo text-lg font-medium tracking-tight text-gray-900 xl:text-xl">
                <ArrowUpRightIcon className="size-[1em] text-accentGreen" />
                {checkInDate}
              </dd>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 bg-white px-4 py-6 sm:px-6 xl:px-8">
              <dt className="text-sm font-medium text-gray-500">
                Check-out Date
              </dt>
              <dd className="flex w-full flex-none items-center gap-2 font-arimo text-lg font-medium tracking-tight text-gray-900 xl:text-xl">
                <ArrowDownRightIcon className="size-[1em] text-accentGreen" />
                {checkOutDate}
              </dd>
            </div>
          </dl>
        </div>
        <div className="mb-10 overflow-hidden rounded-lg bg-white shadow-sm">
          <div className="bg-white p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="font-oswald text-xl font-bold text-gray-900 sm:text-2xl">
                  Room Details
                </p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200"></div>
          <div className="p-6">
            <h3 className="mb-4 text-lg font-medium text-gray-900">
              How many rooms will you need?
            </h3>
            <div className="mb-4 rounded-lg border bg-gray-50 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  Configure your room types and occupancy:
                </span>
              </div>
            </div>

            <Form
              onSubmit={handleFormSubmit}
              initialValues={getInitialConfigurations()}
              validate={(values) => validateRoomQuantities(values, 999)} // Large number since we're not constraining by guests
              mutators={{
                ...arrayMutators,
              }}
              render={({
                handleSubmit,
                hasValidationErrors,
                submitError,
                errors,
                values,
              }) => (
                <form onSubmit={handleSubmit}>
                  {submitError && (
                    <div className="mb-4 text-red-600">{submitError}</div>
                  )}

                  {errors && errors._error && (
                    <div className="mb-4 text-red-600">{errors._error}</div>
                  )}

                  <div className="space-y-6">
                    {/* Single Room Section */}
                    <div className="border-b border-gray-200 pb-6">
                      <div className="mb-4 flex items-center gap-x-3 sm:gap-x-4">
                        <img
                          alt="Single Room"
                          src="https://images.unsplash.com/photo-1631049307264-da0ec9d70304?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                          className="size-12 flex-none rounded-lg bg-gray-50 object-cover sm:size-16"
                        />
                        <div>
                          <p className="text-base font-semibold text-gray-900 sm:text-lg">
                            Single Room
                          </p>
                          <p className="mt-0.5 text-xs text-gray-500 sm:mt-1 sm:text-sm">
                            One bed, bathroom
                          </p>
                        </div>
                      </div>
                      <FieldArray name="single">
                        {({ fields }) => (
                          <div className="space-y-2">
                            {fields.map((name, index) => {
                              return (
                                <div
                                  key={name}
                                  className="rounded-lg border border-gray-200 bg-white p-3 sm:px-4"
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <Field
                                        name={`${name}.rooms`}
                                        component={TextField}
                                        type="number"
                                        placeholder="0"
                                        min="0"
                                        className="block w-20 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-accentGreen focus:ring-1 focus:ring-accentGreen sm:px-3"
                                      />
                                      <span className="whitespace-nowrap text-xs text-gray-500 sm:text-sm">
                                        rooms with
                                      </span>
                                      <Field
                                        name={`${name}.guests`}
                                        component={SelectField}
                                        options={[{ value: '1', label: '1' }]}
                                        className="block w-20 rounded-md border border-gray-300 bg-white px-2 py-1.5 pr-7 text-sm text-gray-900 focus:border-accentGreen focus:ring-1 focus:ring-accentGreen sm:px-3"
                                      />
                                      <span className="whitespace-nowrap text-xs text-gray-500 sm:text-sm">
                                        guest each
                                      </span>
                                    </div>
                                    {fields?.length && fields.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => fields.remove(index)}
                                        className="text-gray-400 hover:text-gray-500"
                                      >
                                        <XMarkIcon className="size-4" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </FieldArray>
                    </div>

                    {/* Double Room Section */}
                    <div className="border-b border-gray-200 pb-6">
                      <div className="mb-4 flex items-center gap-x-3 sm:gap-x-4">
                        <img
                          alt="Double Room"
                          src="https://images.unsplash.com/photo-1673687782286-674e29c9bf9e?q=80&w=2670&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                          className="size-12 flex-none rounded-lg bg-gray-50 object-cover sm:size-16"
                        />
                        <div>
                          <p className="text-base font-semibold text-gray-900 sm:text-lg">
                            Double Room
                          </p>
                          <p className="mt-0.5 text-xs text-gray-500 sm:mt-1 sm:text-sm">
                            Two beds, bathroom
                          </p>
                        </div>
                      </div>
                      <FieldArray name="double">
                        {({ fields }) => (
                          <div className="space-y-2">
                            {fields.map((name, index) => {
                              const guestCount =
                                values?.double?.[index]?.guests || '2';
                              return (
                                <div
                                  key={name}
                                  className="rounded-lg border border-gray-200 bg-white p-3 sm:px-4"
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <Field
                                        name={`${name}.rooms`}
                                        component={TextField}
                                        type="number"
                                        placeholder="0"
                                        min="0"
                                        className="block w-20 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-accentGreen focus:ring-1 focus:ring-accentGreen sm:px-3"
                                      />
                                      <span className="whitespace-nowrap text-xs text-gray-500 sm:text-sm">
                                        rooms with
                                      </span>
                                      <Field
                                        name={`${name}.guests`}
                                        component={SelectField}
                                        options={[
                                          { value: '1', label: '1' },
                                          { value: '2', label: '2' },
                                        ]}
                                        className="block w-20 rounded-md border border-gray-300 bg-white px-2 py-1.5 pr-7 text-sm text-gray-900 focus:border-accentGreen focus:ring-1 focus:ring-accentGreen sm:px-3"
                                      />
                                      <span className="whitespace-nowrap text-xs text-gray-500 sm:text-sm">
                                        {guestCount === '1'
                                          ? 'guest'
                                          : 'guests'}{' '}
                                        each
                                      </span>
                                    </div>
                                    {fields?.length && fields.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => fields.remove(index)}
                                        className="text-gray-400 hover:text-gray-500"
                                      >
                                        <XMarkIcon className="size-4" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            {fields?.length &&
                              fields.length <
                                MAX_CONFIGURATIONS_PER_ROOM_TYPE && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    fields.push({ rooms: '0', guests: '2' })
                                  }
                                  className="mt-2 flex items-center gap-1 text-xs text-secondaryBlue hover:text-blue-700 sm:text-sm"
                                >
                                  <PlusIcon className="size-3 sm:size-4" />
                                  Add another configuration
                                </button>
                              )}
                          </div>
                        )}
                      </FieldArray>
                    </div>

                    {/* Suite Room Section */}
                    <div className="pb-6">
                      <div className="mb-4 flex items-center gap-x-3 sm:gap-x-4">
                        <img
                          alt="Suite Room"
                          src="https://images.unsplash.com/photo-1611892440504-42a792e24d32?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                          className="size-12 flex-none rounded-lg bg-gray-50 object-cover sm:size-16"
                        />
                        <div>
                          <p className="text-base font-semibold text-gray-900 sm:text-lg">
                            Suite Room
                          </p>
                          <p className="mt-0.5 text-xs text-gray-500 sm:mt-1 sm:text-sm">
                            One bed, living room, bathroom
                          </p>
                        </div>
                      </div>
                      <FieldArray name="suite">
                        {({ fields }) => (
                          <div className="space-y-2">
                            {fields.map((name, index) => {
                              const guestCount =
                                values?.suite?.[index]?.guests || '1';
                              return (
                                <div
                                  key={name}
                                  className="rounded-lg border border-gray-200 bg-white p-3 sm:px-4"
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <Field
                                        name={`${name}.rooms`}
                                        component={TextField}
                                        type="number"
                                        placeholder="0"
                                        min="0"
                                        className="block w-20 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-accentGreen focus:ring-1 focus:ring-accentGreen sm:px-3"
                                      />
                                      <span className="whitespace-nowrap text-xs text-gray-500 sm:text-sm">
                                        rooms with
                                      </span>
                                      <Field
                                        name={`${name}.guests`}
                                        component={SelectField}
                                        options={[
                                          { value: '1', label: '1' },
                                          { value: '2', label: '2' },
                                        ]}
                                        className="block w-20 rounded-md border border-gray-300 bg-white px-2 py-1.5 pr-7 text-sm text-gray-900 focus:border-accentGreen focus:ring-1 focus:ring-accentGreen sm:px-3"
                                      />
                                      <span className="whitespace-nowrap text-xs text-gray-500 sm:text-sm">
                                        {guestCount === '1'
                                          ? 'guest'
                                          : 'guests'}{' '}
                                        each
                                      </span>
                                    </div>
                                    {fields?.length && fields.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => fields.remove(index)}
                                        className="text-gray-400 hover:text-gray-500"
                                      >
                                        <XMarkIcon className="size-4" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            {fields?.length &&
                              fields.length <
                                MAX_CONFIGURATIONS_PER_ROOM_TYPE && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    fields.push({ rooms: '0', guests: '1' })
                                  }
                                  className="mt-2 flex items-center gap-1 text-xs text-secondaryBlue hover:text-blue-700 sm:text-sm"
                                >
                                  <PlusIcon className="size-3 sm:size-4" />
                                  Add another configuration
                                </button>
                              )}
                          </div>
                        )}
                      </FieldArray>
                    </div>
                  </div>

                  {/* Total Summary */}
                  {values && (
                    <div className="mt-6 rounded-lg bg-blue-50 p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-blue-900">
                          Current Selection:
                        </span>
                        <span className="text-sm text-blue-700">
                          {(() => {
                            const singleTotals = calculateTotalsFromConfigs(
                              values.single || [],
                            );
                            const doubleTotals = calculateTotalsFromConfigs(
                              values.double || [],
                            );
                            const suiteTotals = calculateTotalsFromConfigs(
                              values.suite || [],
                            );
                            const totalRooms =
                              singleTotals.rooms +
                              doubleTotals.rooms +
                              suiteTotals.rooms;
                            const totalGuests =
                              singleTotals.guests +
                              doubleTotals.guests +
                              suiteTotals.guests;
                            return `${totalRooms} room${totalRooms !== 1 ? 's' : ''}, ${totalGuests} guest${totalGuests !== 1 ? 's' : ''}`;
                          })()}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 flex justify-end">
                    <TealButton
                      text="Submit"
                      type="submit"
                      disabled={hasValidationErrors}
                      className="px-4 py-2 text-lg"
                    />
                  </div>
                </form>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupRooms;
